import React, { useState } from "react";
import { Banknote, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Request } from "@/types/request";
import { useMarkServiceRequestPaidOffline } from "../hooks/useRequests";
import { canMarkRequestPaidOffline } from "../utils/paymentStatus";

type MarkOfflinePaymentButtonProps = {
  request: Request;
  compact?: boolean;
  onSuccess?: () => void;
};

export const MarkOfflinePaymentButton: React.FC<
  MarkOfflinePaymentButtonProps
> = ({ request, compact = false, onSuccess }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const markPaidOffline = useMarkServiceRequestPaidOffline();
  const canMarkPaidOffline = canMarkRequestPaidOffline(request);

  if (!canMarkPaidOffline) {
    return null;
  }

  const handleConfirm = async () => {
    try {
      await markPaidOffline.mutateAsync(request.id);
      toast.success(t("requests.payment.markPaidOfflineSuccess"));
      setOpen(false);
      onSuccess?.();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t("requests.payment.markPaidOfflineFailed");
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant={compact ? "new_ghost" : "new_outline"}
          size={compact ? "sm" : "default"}
          className={compact ? "h-8 w-8 p-0" : undefined}
          title={t("requests.payment.markPaidOffline")}
          onClick={(event) => event.stopPropagation()}
        >
          <Banknote className="h-4 w-4" />
          {compact ? null : t("requests.payment.markPaidOffline")}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-white"
        onClick={(event) => event.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{t("requests.payment.confirmOfflineTitle")}</DialogTitle>
          <DialogDescription>
            {t("requests.payment.confirmOfflineDescription", {
              serviceName: request.service?.name ?? t("requests.form.service"),
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="new_outline"
            onClick={() => setOpen(false)}
            disabled={markPaidOffline.isPending}
          >
            {t("requests.form.cancel")}
          </Button>
          <Button
            type="button"
            variant="new_default"
            onClick={() => void handleConfirm()}
            disabled={markPaidOffline.isPending}
          >
            {markPaidOffline.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            {markPaidOffline.isPending
              ? t("requests.payment.markingPaidOffline")
              : t("requests.payment.confirmOfflineAction")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
